import { computed, makeObservable } from 'mobx'
import type { Moment } from 'moment'
import moment from 'moment'

import { Model } from 'modules/core/lib/mobx-rest'

import type { PolicyTarget } from 'modules/permissions/store/collections/policyAuthorizations'

export const Status = {
  draft: 'draft',
  scheduled: 'scheduled',
  active: 'active',
  finished: 'finished',
}

interface Respondents {
  [key: string]: number | null | string | number[] | undefined
  access_ids: number[]
}

enum Roles {
  author = 'author',
  manager = 'manager',
  supervisor = 'supervisor',
  team_lead = 'team_lead',
}

interface UserRoles {
  [Roles.author]: boolean
  [Roles.manager]: boolean
  [Roles.supervisor]: boolean
  [Roles.team_lead]: boolean
}

interface CycleInfo {
  id: number
  closed_at: string | null
  ends_at: string | null
  number_of_answers: number
}

export interface ISurvey {
  id: number
  access_id: number
  name: string
  start_on: string
  end_on: string
  frequency: string
  status: string
  description: string
  source: string
  visible_to_managers: boolean
  anonymous: boolean
  author_id: number
  supervisor_ids: number[]
  duration_in_days: string
  scheduled_time: string
  question_groups_count: number
  answered_questions_count: number
  role: UserRoles
  current_cycle: CycleInfo
}

class Survey extends Model<ISurvey> implements PolicyTarget {
  static Status = Status

  constructor(attr: ISurvey) {
    super(attr)
    makeObservable(this, {
      isFinished: computed,
      isDraft: computed,
      isScheduled: computed,
      isActive: computed,
      isAnonymous: computed,
      answeredQuestionsCount: computed,
    })
  }

  get policyTargetClass(): string {
    return '::Surveys::V2::Survey'
  }

  get indexes() {
    return ['access_id', 'surveys_form_id']
  }

  static urlRoot() {
    return '/surveys/v2/surveys'
  }

  startSurvey() {
    return this.rpc('start')
  }

  stopSurvey() {
    return this.rpc('stop')
  }

  remindParticipants() {
    return this.rpc('notify')
  }

  get roles(): UserRoles {
    return this.attributes.role
  }

  get isDraft(): boolean {
    return this.attributes.status === Status.draft
  }

  get isScheduled(): boolean {
    return this.attributes.status === Status.scheduled
  }

  get isActive(): boolean {
    return this.attributes.status === Status.active
  }

  get isFinished(): boolean {
    return this.attributes.status === Status.finished
  }

  get isAnonymous(): boolean {
    return this.attributes.anonymous
  }

  get isStartOnSet(): boolean {
    return this.attributes.start_on != null
  }

  get isScheduleTimeSet(): boolean {
    return this.attributes.scheduled_time != null
  }

  get startOnUtcDateTime(): Moment | undefined {
    if (this.isStartOnSet && this.isScheduleTimeSet) {
      return moment.utc(this.attributes.start_on + ' ' + this.attributes.scheduled_time)
    } else {
      return undefined
    }
  }

  get isDaysToAnswerSet(): boolean {
    return this.attributes.duration_in_days != null
  }

  get answeredQuestionsCount(): number {
    return this.attributes.answered_questions_count
  }

  get hasSchedule(): boolean {
    return this.attributes.frequency !== 'single'
  }

  get hasAnswers(): boolean {
    return this.attributes.answered_questions_count > 0
  }

  fetchRespondents(): Promise<Respondents> {
    return this.rpc('respondents')
  }

  get currentCycleId(): number {
    return this.attributes.current_cycle.id
  }

  get cycleHasAnswers(): boolean {
    return this.attributes.current_cycle.number_of_answers > 0
  }

  get isWorkflowSurvey(): boolean {
    return this.attributes.source === 'workflow_survey'
  }
}

export default Survey
